/**
 * usePublicDetailNavigation
 *
 * Browser-style pill-tab navigation for the public artifact detail panel.
 * Mirrors the authenticated `useTaskDetailsPanelNavigation` /
 * `useNoteDetailPanelNavigation` pair, minus map zoom and store selection —
 * on public pages the selection is purely URL-driven, so every navigation
 * goes through `openArtifact` (which routes to `/share/:token/:singular/:itemId`).
 *
 * Reuses the shared `taskNavigation` Zustand slice + `NavigationTabs` strip,
 * so task↔note hops swap the panel content in place with closable tabs —
 * exactly like the logged-in app.
 *
 * @module components/share/public/usePublicDetailNavigation
 */
import { useCallback, useEffect, type MouseEvent } from 'react';

import { useNavigationTabs } from '@/hooks';
import { useArtifactNavigation } from '@/hooks/useArtifactNavigation';
import { useGeoMarkupStore } from '@/store';

import type { PublicArtifactDescriptor } from './usePublicArtifactSelection';

/** Minimal artifact reference needed to open a navigation tab. */
export interface PublicNavigationTarget {
  id: string;
  shortId: string;
  title: string;
}

type TabKind = 'task' | 'note';

function tabArtifactId(type: TabKind): 'tasks' | 'notes' {
  return type === 'note' ? 'notes' : 'tasks';
}

export function usePublicDetailNavigation(
  descriptor: PublicArtifactDescriptor | null,
  onPanelClose: () => void
) {
  const { tabs, activeTabId, openTab, closeTab, switchTab, closeAllTabs } =
    useNavigationTabs();
  const { openArtifact } = useArtifactNavigation();

  const findTab = useCallback(
    (target: { id: string; shortId: string }) =>
      tabs.find(
        (tab) => tab.taskId === target.id || tab.shortId === target.shortId
      ),
    [tabs]
  );

  /** Opens (or switches to) a tab for the given artifact and navigates to it. */
  const openArtifactTab = useCallback(
    (kind: TabKind, target: PublicNavigationTarget) => {
      const existingTab = findTab(target);

      if (existingTab) {
        switchTab(existingTab.id);
      } else {
        // Seed a tab for the currently open artifact first, so the strip
        // shows both the origin and the destination (authenticated parity).
        if (
          tabs.length === 0 &&
          descriptor &&
          descriptor.kind !== 'obstruction'
        ) {
          openTab(
            descriptor.id,
            descriptor.title,
            descriptor.kind,
            descriptor.shortId
          );
        }
        openTab(target.id, target.title, kind, target.shortId);
      }

      openArtifact(tabArtifactId(kind), 'detail', target.shortId);
    },
    [findTab, switchTab, tabs.length, descriptor, openTab, openArtifact]
  );

  const handleViewNote = useCallback(
    (note: PublicNavigationTarget) => openArtifactTab('note', note),
    [openArtifactTab]
  );

  const handleViewTask = useCallback(
    (task: PublicNavigationTarget) => openArtifactTab('task', task),
    [openArtifactTab]
  );

  const handleTabClick = useCallback(
    (tabId: string) => {
      const freshState = useGeoMarkupStore.getState();
      const tab = freshState.taskNavigation.tabs.find(
        (navTab) => navTab.id === tabId
      );
      if (!tab) return;

      switchTab(tabId);
      openArtifact(
        tabArtifactId(tab.type === 'note' ? 'note' : 'task'),
        'detail',
        tab.shortId ?? tab.taskId
      );
    },
    [switchTab, openArtifact]
  );

  const handleTabClose = useCallback(
    (tabId: string, event: MouseEvent) => {
      event.stopPropagation();
      closeTab(tabId);

      const freshState = useGeoMarkupStore.getState();
      const newActiveTab = freshState.taskNavigation.tabs.find(
        (navTab) => navTab.id === freshState.taskNavigation.activeTabId
      );

      if (newActiveTab) {
        openArtifact(
          tabArtifactId(newActiveTab.type === 'note' ? 'note' : 'task'),
          'detail',
          newActiveTab.shortId ?? newActiveTab.taskId
        );
      } else {
        closeAllTabs();
        onPanelClose();
      }
    },
    [closeTab, openArtifact, closeAllTabs, onPanelClose]
  );

  /** Panel close (X / Escape) — clears the tab strip along with the URL. */
  const handlePanelClose = useCallback(() => {
    closeAllTabs();
    onPanelClose();
  }, [closeAllTabs, onPanelClose]);

  // Selection is URL-driven (browser back/forward bypasses the handlers
  // above) — keep the active tab in sync with whatever the URL selected.
  useEffect(() => {
    if (!descriptor || descriptor.kind === 'obstruction') {
      if (tabs.length > 0) {
        closeAllTabs();
      }
      return;
    }
    if (tabs.length === 0) return;

    const matching = tabs.find(
      (tab) =>
        tab.taskId === descriptor.id || tab.shortId === descriptor.shortId
    );
    if (matching) {
      if (matching.id !== activeTabId) {
        switchTab(matching.id);
      }
    } else {
      openTab(
        descriptor.id,
        descriptor.title,
        descriptor.kind,
        descriptor.shortId
      );
    }
  }, [descriptor, tabs, activeTabId, switchTab, openTab, closeAllTabs]);

  return {
    tabs,
    activeTabId,
    handleTabClick,
    handleTabClose,
    handlePanelClose,
    handleViewTask,
    handleViewNote,
  };
}
